import { Box, Button, Grid, Link, Typography } from '@mui/material';
import React from 'react';
import NavButton from './NavButton';
import useQueryParams from '../../hooks/useQueryParams';
import { router } from '@inertiajs/react';
import Card from './Card';
import ButtonLink from '../../components/ButtonLink';
import ProductsList from './ProductsList';
import ProgramsAndIncentives from './ProgramsAndIncentives';
import filterIncentivesByTags from '../../utils/filterIncentivesByTags';

const BUTTON_STYLES = {
  alignSelf: 'flex-start',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.875rem',
  width: { xs: '100%', sm: 'auto' },
};

const NumberCircle = ({ children }) => (
  <Box
    sx={{
      height: '35px',
      width: '35px',
      borderRadius: '50%',
      bgcolor: 'blue.450',
      color: 'common.white',
      fontFamily: 'Outfit',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      mb: 0.5,
    }}
  >
    {children}
  </Box>
);

export default function NextStepsStep({
  myProject,
  room,
  onBack,
  onEditSelections,
  productsByRating,
  incentives,
  resources,
  buildProductDrawerClickHandler,
  buildResourceDrawerClickHandler,
  incentiveImgix,
}) {
  const { queryParams } = useQueryParams();

  const incentivesForRoom = incentives
    ? filterIncentivesByTags(
        incentives.incentives,
        room.upfront_costs.flatMap((cost) => cost.incentive_tags),
      )
    : [];

  const showProducts = room.show_products && productsByRating && productsByRating.length > 0;
  const showPrograms =
    room.show_resources && (incentivesForRoom.length > 0 || (resources && resources.length > 0));

  const onRoomClick = (e, room) => {
    e.preventDefault();
    router.visit(`/electrify/${room.handle}?${queryParams.toString()}`, {
      preserveScroll: true,
      preserveState: true,
    });
  };

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={{ xs: 'column', sm: 'row' }}
        spacing={2}
        mb={2}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h3" fontWeight={600}>
            Your Personalized Next Steps
          </Typography>
        </Box>
        <Button
          onClick={onEditSelections}
          sx={{
            fontWeight: 700,
            textDecoration: 'underline',
            textTransform: 'none',
            color: 'common.black',
            alignSelf: { xs: 'flex-start', sm: 'center' },
            pl: { xs: 0, sm: 2 },
            '&:hover': {
              textDecoration: 'underline',
            },
            '&:focus-visible': {
              outline: '2px solid',
            },
          }}
        >
          Edit Selections
        </Button>
      </Box>

      <Box>
        <Box
          component="nav"
          role="tablist"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'grey.300',
            display: 'flex',
            overflowX: 'scroll',
          }}
        >
          {myProject.map((r) => (
            <Link
              key={r.handle}
              onClick={(e) => onRoomClick(e, r)}
              tabIndex={0}
              sx={{
                display: 'block',
                borderBottom: '3px solid',
                borderColor: r.handle === room.handle ? 'orange.500' : 'transparent',
                textDecoration: 'none',
                fontFamily: 'Outfit',
                fontWeight: 500,
                color: 'common.black',
                p: 2,
                cursor: 'pointer',
                minWidth: { xs: '100px', md: 'auto' },
                '&:hover': {
                  bgcolor: 'grey.100',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                },
              }}
            >
              {r.title}
            </Link>
          ))}
        </Box>

        <Box mt={{ xs: 3, sm: 5 }}>
          <Grid container columnSpacing={4}>
            <Grid
              item
              xs={0}
              sm={1}
              display={{ xs: 'none', sm: 'flex' }}
              flexDirection="column"
              alignItems="center"
            >
              <NumberCircle>1</NumberCircle>
              <Box sx={{ width: '1px', bgcolor: 'grey.300', flexGrow: 1, mb: 0.5 }} />
            </Grid>
            <Grid item xs={12} sm={11}>
              <Box pb={3}>
                <Card>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="h3" fontWeight={600} mb={0.5}>
                      Get a Personalized Estimate
                    </Typography>
                    <Typography mb={3}>
                      Log into your PG&E account to see costs and savings tailored to your home,
                      usage, and rate plan.
                    </Typography>
                    <ButtonLink
                      external
                      href="https://www.pge.com/en/clean-energy/clean-energy-calculator.html"
                      variant="primary"
                      sx={BUTTON_STYLES}
                    >
                      Clean Energy Calculator
                    </ButtonLink>
                  </Box>
                </Card>
              </Box>
            </Grid>

            {showProducts && (
              <>
                <Grid
                  item
                  xs={0}
                  sm={1}
                  display={{ xs: 'none', sm: 'flex' }}
                  flexDirection="column"
                  alignItems="center"
                >
                  <NumberCircle>2</NumberCircle>
                  <Box sx={{ width: '1px', bgcolor: 'grey.300', flexGrow: 1, mb: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={11}>
                  <Box pb={3}>
                    <Card>
                      <ProductsList
                        room={room}
                        productsByRating={productsByRating}
                        buildOnClick={buildProductDrawerClickHandler}
                      />
                    </Card>
                  </Box>
                </Grid>
              </>
            )}

            {showPrograms && (
              <>
                <Grid
                  item
                  xs={0}
                  sm={1}
                  display={{ xs: 'none', sm: 'flex' }}
                  flexDirection="column"
                  alignItems="center"
                >
                  <NumberCircle>{showProducts ? 3 : 2}</NumberCircle>
                  <Box sx={{ width: '1px', bgcolor: 'grey.300', flexGrow: 1, mb: 0.5 }} />
                </Grid>
                <Grid item xs={12} sm={11}>
                  <Box pb={3}>
                    <Card>
                      <ProgramsAndIncentives
                        room={room}
                        incentivesForRoom={incentivesForRoom}
                        resources={resources}
                        incentiveImgix={incentiveImgix}
                        buildOnClick={buildResourceDrawerClickHandler}
                      />
                    </Card>
                  </Box>
                </Grid>
              </>
            )}

            <Grid
              item
              xs={0}
              sm={1}
              display={{ xs: 'none', sm: 'flex' }}
              flexDirection="column"
              alignItems="center"
            >
              <NumberCircle>
                {showProducts && showPrograms ? 4 : showProducts || showPrograms ? 3 : 2}
              </NumberCircle>
            </Grid>
            <Grid item xs={12} sm={11}>
              <Box pb={3}>
                <Card>
                  <Box display="flex" flexDirection="column">
                    <Typography variant="h3" fontWeight={600} mb={0.5}>
                      Go to PG&E&apos;s Project Portal
                    </Typography>
                    <Typography mb={2}>
                      Use the Project Portal to notify PG&E you&apos;re upgrading and ensure your
                      home has enough power. Then find a qualified installer.
                    </Typography>
                    <ButtonLink
                      external
                      href="https://www.pge.com/en/account/service-requests/building-and-renovation.html"
                      variant="primary"
                      sx={BUTTON_STYLES}
                    >
                      Project Portal
                    </ButtonLink>
                  </Box>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <NavButton onClick={onBack} variant="secondary" sx={{ maxWidth: 'none' }}>
        Go Back
      </NavButton>
    </Box>
  );
}
